// Determine the API URL dynamically for custom domain support
// If running on custom domain, use that domain for API calls
// Otherwise, use the environment variable
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // In browser environment, check if we're on a custom domain
  if (typeof window !== 'undefined') {
    const currentHost = window.location.origin;
    const previewPattern = /\.preview\.emergentagent\.com$/;
    
    // If we're NOT on a preview domain, use the current host for API calls
    // This allows custom domains to work correctly
    if (!previewPattern.test(window.location.hostname) && 
        window.location.hostname !== 'localhost' &&
        !window.location.hostname.includes('127.0.0.1')) {
      return currentHost;
    }
  }
  
  return envUrl;
};

const API_URL = getApiUrl();

class ApiService {
  constructor() {
    this.token = sessionStorage.getItem('token');  // via secureStorage pattern
  }

  setToken(token) {
    this.token = token;
    if (token) {
      sessionStorage.setItem('token', token);
    }
  }

  getToken() {
    if (!this.token) {
      this.token = sessionStorage.getItem('token');  // via secureStorage pattern
    }
    return this.token;
  }

  // Universal XHR request method - avoids all fetch/body stream issues
  request(endpoint, options = {}) {
    const url = `${API_URL}/api${endpoint}`;
    const method = options.method || 'GET';
    const token = this.getToken();
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      
      // Set headers
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        let data = {};
        try {
          if (xhr.responseText) {
            data = JSON.parse(xhr.responseText);
          }
        } catch (e) {
          data = { message: xhr.responseText };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          reject(new Error(data.detail || data.message || `Request failed: ${xhr.status}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.ontimeout = function() {
        reject(new Error('Request timeout'));
      };
      
      // Send with body for non-GET requests
      if (options.body && method !== 'GET') {
        const bodyStr = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
        xhr.send(bodyStr);
      } else {
        xhr.send();
      }
    });
  }

  // Auth endpoints
  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: { email, password }
    });
  }

  async register(email, password, name, role = 'engineer') {
    return this.request('/auth/register', {
      method: 'POST',
      body: { email, password, name, role }
    });
  }

  async getMe() {
    return this.request('/auth/me');
  }

  async updateProfile(profileData) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: profileData
    });
  }

  // Dashboard stats
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  async getLiveMetrics() {
    return this.request('/dashboard/live-metrics');
  }

  // Parts
  async getParts() {
    return this.request('/parts');
  }

  async getPart(id) {
    return this.request(`/parts/${id}`);
  }

  async getPartWithLinks(id) {
    return this.request(`/parts/${id}/with-links`);
  }

  async createPart(partData) {
    return this.request('/parts', {
      method: 'POST',
      body: partData
    });
  }

  async updatePart(id, partData) {
    return this.request(`/parts/${id}`, {
      method: 'PUT',
      body: partData
    });
  }

  async deletePart(id) {
    return this.request(`/parts/${id}`, { method: 'DELETE' });
  }

  async linkPartToECO(partId, ecoId) {
    return this.request(`/parts/${partId}/link-eco/${ecoId}`, { method: 'POST' });
  }

  async unlinkPartFromECO(partId, ecoId) {
    return this.request(`/parts/${partId}/link-eco/${ecoId}`, { method: 'DELETE' });
  }

  async linkPartToCI(partId, ciId) {
    return this.request(`/parts/${partId}/link-ci/${ciId}`, { method: 'POST' });
  }

  async unlinkPartFromCI(partId, ciId) {
    return this.request(`/parts/${partId}/link-ci/${ciId}`, { method: 'DELETE' });
  }

  // CDRLs
  async getCDRLs() {
    return this.request('/cdrls');
  }

  async getCDRL(id) {
    return this.request(`/cdrls/${id}`);
  }

  async createCDRL(cdrlData) {
    return this.request('/cdrls', {
      method: 'POST',
      body: cdrlData
    });
  }

  async updateCDRL(id, cdrlData) {
    return this.request(`/cdrls/${id}`, {
      method: 'PUT',
      body: cdrlData
    });
  }

  async deleteCDRL(id) {
    return this.request(`/cdrls/${id}`, { method: 'DELETE' });
  }

  // ECOs
  async getECOs() {
    return this.request('/ecos');
  }

  async getECO(id) {
    return this.request(`/ecos/${id}`);
  }

  async getECOWithLinks(id) {
    return this.request(`/ecos/${id}/with-links`);
  }

  async createECO(ecoData) {
    return this.request('/ecos', {
      method: 'POST',
      body: ecoData
    });
  }

  async submitECO(id) {
    return this.request(`/ecos/${id}/submit`, { method: 'POST' });
  }

  async approveECO(id, comment = '') {
    return this.request(`/ecos/${id}/approve`, {
      method: 'POST',
      body: { comment }
    });
  }

  async rejectECO(id, reason) {
    return this.request(`/ecos/${id}/reject`, {
      method: 'POST',
      body: { reason }
    });
  }

  async reviseECO(id) {
    return this.request(`/ecos/${id}/revise`, { method: 'POST' });
  }

  async releaseECO(id) {
    return this.request(`/ecos/${id}/release`, { method: 'POST' });
  }

  // SDRLs
  async getSDRLs() {
    return this.request('/sdrls');
  }

  async getSDRL(id) {
    return this.request(`/sdrls/${id}`);
  }

  async createSDRL(sdrlData) {
    return this.request('/sdrls', {
      method: 'POST',
      body: sdrlData
    });
  }

  // Deviations
  async getDeviations() {
    return this.request('/deviations');
  }

  async createDeviation(deviationData) {
    return this.request('/deviations', {
      method: 'POST',
      body: deviationData
    });
  }

  // Programs
  async getPrograms() {
    return this.request('/programs');
  }

  async getProgram(id) {
    return this.request(`/programs/${id}`);
  }

  // Requirements
  async getRequirements() {
    return this.request('/requirements');
  }

  async getRequirementsHierarchy() {
    return this.request('/requirements/hierarchy');
  }

  async getRequirement(id) {
    return this.request(`/requirements/${id}`);
  }

  async getRequirementTrace(id) {
    return this.request(`/requirements/${id}/trace`);
  }

  async getRequirementIssues() {
    return this.request('/requirements/flags/issues');
  }

  async createRequirement(reqData) {
    return this.request('/requirements', {
      method: 'POST',
      body: reqData
    });
  }

  async updateRequirement(id, reqData) {
    return this.request(`/requirements/${id}`, {
      method: 'PUT',
      body: reqData
    });
  }

  async deleteRequirement(id) {
    return this.request(`/requirements/${id}`, { method: 'DELETE' });
  }

  async linkRequirementToECO(reqId, ecoId) {
    return this.request(`/requirements/${reqId}/link-eco/${ecoId}`, { method: 'POST' });
  }

  async initiateECOFromRequirement(reqId) {
    return this.request(`/requirements/${reqId}/initiate-eco`, { method: 'POST' });
  }

  async getRequirementCCBStatus(reqId) {
    return this.request(`/requirements/${reqId}/ccb-status`);
  }

  // Analytics
  async getAnalytics() {
    return this.request('/analytics');
  }

  // Notifications
  async getNotifications() {
    return this.request('/notifications');
  }

  // Email config
  async getEmailConfig() {
    return this.request('/email/config');
  }

  // Tenant management
  async createTenant(tenantData) {
    return this.request('/tenants', {
      method: 'POST',
      body: tenantData
    });
  }

  async getTenants() {
    return this.request('/tenants');
  }

  // Audit logs
  async getAuditLogs(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params.limit) queryParams.append('limit', params.limit);
    const queryString = queryParams.toString();
    return this.request(`/audit-logs${queryString ? '?' + queryString : ''}`);
  }

  // Demo request
  async submitDemoRequest(demoData) {
    return this.request('/demo-request', {
      method: 'POST',
      body: demoData
    });
  }

  // Copilot chat — backend Pydantic model uses snake_case `session_id`.
  async copilotChat(message, sessionId, context = {}) {
    return this.request('/copilot/chat', {
      method: 'POST',
      body: { message, session_id: sessionId, context }
    });
  }

  // Clear copilot history
  async clearCopilotHistory() {
    return this.request('/copilot/history', {
      method: 'DELETE'
    });
  }

  // ==================== SPACE / SATELLITE COMMANDS ====================

  // Fetch the per-subsystem allowed command catalog for a satellite
  async getSatelliteCommandCatalog(satelliteId) {
    return this.request(`/space/satellites/${satelliteId}/commands/catalog`);
  }

  // Queue a command to a satellite (subsystem, command_type, params, priority)
  async sendSatelliteCommand(satelliteId, command) {
    return this.request(`/space/satellites/${satelliteId}/commands`, {
      method: 'POST',
      body: command,
    });
  }

  // List recent commands issued to a satellite
  async listSatelliteCommands(satelliteId, limit = 30) {
    return this.request(`/space/satellites/${satelliteId}/commands?limit=${limit}`);
  }

  // ==================== SECURITY ENDPOINTS ====================

  // Get live security status (NIST 800-171 controls)
  async getSecurityStatus() {
    return this.request('/security/status');
  }

  // Get security audit logs
  async getSecurityLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/security/logs${queryString ? `?${queryString}` : ''}`);
  }

  // Change password with NIST complexity requirements
  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      })
    });
  }

  // ==================== QUALITY MANAGEMENT APIs ====================

  // NCR APIs
  async getNCRs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/quality/ncrs${queryString ? `?${queryString}` : ''}`);
  }

  async getNCR(ncrId) {
    return this.request(`/quality/ncrs/${ncrId}`);
  }

  async createNCR(ncrData) {
    return this.request('/quality/ncrs', {
      method: 'POST',
      body: JSON.stringify(ncrData)
    });
  }

  async updateNCR(ncrId, ncrData) {
    return this.request(`/quality/ncrs/${ncrId}`, {
      method: 'PUT',
      body: JSON.stringify(ncrData)
    });
  }

  async assignNCR(ncrId, assignee) {
    return this.request(`/quality/ncrs/${ncrId}/assign?assignee=${encodeURIComponent(assignee)}`, {
      method: 'POST'
    });
  }

  async escalateNCR(ncrId, reason = '') {
    return this.request(`/quality/ncrs/${ncrId}/escalate?reason=${encodeURIComponent(reason)}`, {
      method: 'POST'
    });
  }

  async submitNCRToMRB(ncrId, disposition = '', justification = '') {
    return this.request(`/quality/ncrs/${ncrId}/submit-mrb?disposition=${encodeURIComponent(disposition)}&justification=${encodeURIComponent(justification)}`, {
      method: 'POST'
    });
  }

  async closeNCR(ncrId, closedBy = 'System') {
    return this.request(`/quality/ncrs/${ncrId}/close?closedBy=${encodeURIComponent(closedBy)}`, {
      method: 'POST'
    });
  }

  // CAPA APIs
  async getCAPAs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/quality/capas${queryString ? `?${queryString}` : ''}`);
  }

  async getCAPA(capaId) {
    return this.request(`/quality/capas/${capaId}`);
  }

  async createCAPA(capaData) {
    return this.request('/quality/capas', {
      method: 'POST',
      body: JSON.stringify(capaData)
    });
  }

  async updateCAPA(capaId, capaData) {
    return this.request(`/quality/capas/${capaId}`, {
      method: 'PUT',
      body: JSON.stringify(capaData)
    });
  }

  async startCAPA(capaId) {
    return this.request(`/quality/capas/${capaId}/start`, {
      method: 'POST'
    });
  }

  async updateCAPAProgress(capaId, notes = '') {
    return this.request(`/quality/capas/${capaId}/update-progress?notes=${encodeURIComponent(notes)}`, {
      method: 'POST'
    });
  }

  async verifyCAPAEffectiveness(capaId, verified = true, notes = '') {
    return this.request(`/quality/capas/${capaId}/verify?verified=${verified}&notes=${encodeURIComponent(notes)}`, {
      method: 'POST'
    });
  }

  async closeCAPA(capaId, closedBy = 'System') {
    return this.request(`/quality/capas/${capaId}/close?closedBy=${encodeURIComponent(closedBy)}`, {
      method: 'POST'
    });
  }

  // MRB APIs
  async getMRBs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/quality/mrbs${queryString ? `?${queryString}` : ''}`);
  }

  async getMRB(mrbId) {
    return this.request(`/quality/mrbs/${mrbId}`);
  }

  async approveMRB(mrbId, role, approver, comments = '') {
    return this.request(`/quality/mrbs/${mrbId}/approve?role=${encodeURIComponent(role)}&approver=${encodeURIComponent(approver)}&comments=${encodeURIComponent(comments)}`, {
      method: 'POST'
    });
  }

  async rejectMRB(mrbId, role, approver, reason = '') {
    return this.request(`/quality/mrbs/${mrbId}/reject?role=${encodeURIComponent(role)}&approver=${encodeURIComponent(approver)}&reason=${encodeURIComponent(reason)}`, {
      method: 'POST'
    });
  }

  // Work Order APIs
  async getWorkOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/quality/work-orders${queryString ? `?${queryString}` : ''}`);
  }

  async getWorkOrder(woId) {
    return this.request(`/quality/work-orders/${woId}`);
  }

  async createWorkOrder(woData) {
    return this.request('/quality/work-orders', {
      method: 'POST',
      body: JSON.stringify(woData)
    });
  }

  async startWorkOrder(woId) {
    return this.request(`/quality/work-orders/${woId}/start`, {
      method: 'POST'
    });
  }

  async pauseWorkOrder(woId, reason = '') {
    return this.request(`/quality/work-orders/${woId}/pause?reason=${encodeURIComponent(reason)}`, {
      method: 'POST'
    });
  }

  async resumeWorkOrder(woId) {
    return this.request(`/quality/work-orders/${woId}/resume`, {
      method: 'POST'
    });
  }

  async recordWorkOrderUnit(woId) {
    return this.request(`/quality/work-orders/${woId}/record-unit`, {
      method: 'POST'
    });
  }

  async cancelWorkOrder(woId, reason = '') {
    return this.request(`/quality/work-orders/${woId}/cancel?reason=${encodeURIComponent(reason)}`, {
      method: 'POST'
    });
  }

  async closeWorkOrder(woId) {
    return this.request(`/quality/work-orders/${woId}/close`, {
      method: 'POST'
    });
  }

  // Quality Dashboard Stats
  async getQualityDashboardStats() {
    return this.request('/quality/dashboard/stats');
  }

  // ==================== SAAS SUBSCRIPTION APIs ====================

  // Get subscription plans
  async getSubscriptionPlans() {
    return this.request('/saas/plans');
  }

  // Get specific plan details
  async getPlanDetails(planId) {
    return this.request(`/saas/plans/${planId}`);
  }

  // Create checkout session for subscription
  async createCheckoutSession(planId, tenantId, originUrl) {
    return this.request(`/saas/checkout/create?plan_id=${planId}&tenant_id=${tenantId}&origin_url=${encodeURIComponent(originUrl)}`, {
      method: 'POST'
    });
  }

  // Get checkout session status
  async getCheckoutStatus(sessionId) {
    return this.request(`/saas/checkout/status/${sessionId}`);
  }

  // Get tenant subscription details
  async getTenantSubscription(tenantId) {
    return this.request(`/saas/subscription/${tenantId}`);
  }

  // Get tenant usage stats
  async getTenantUsage(tenantId) {
    return this.request(`/saas/usage/${tenantId}`);
  }

  // Check if tenant can create a resource (feature gating)
  async canCreateResource(tenantId, resourceType) {
    return this.request(`/saas/can-create/${tenantId}/${resourceType}`);
  }

  // Generate organization code for team invitations
  async generateOrgCode(tenantId, defaultRole = 'viewer', maxUses = -1, expiresDays = null) {
    let url = `/saas/org-code/generate?tenant_id=${tenantId}&default_role=${defaultRole}&max_uses=${maxUses}`;
    if (expiresDays) {
      url += `&expires_days=${expiresDays}`;
    }
    return this.request(url, { method: 'POST' });
  }

  // Validate organization code
  async validateOrgCode(code) {
    return this.request(`/saas/org-code/validate/${code}`);
  }

  // ==================== SAAS AUTH APIs ====================

  // SaaS Registration (new org or join with code)
  async registerSaaS(registrationData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: registrationData
    });
  }

  // Request password reset
  async requestPasswordReset(email, originUrl = null) {
    return this.request('/auth/password-reset/request', {
      method: 'POST',
      body: { email, origin_url: originUrl }
    });
  }

  // Confirm password reset
  async confirmPasswordReset(token, newPassword) {
    return this.request('/auth/password-reset/confirm', {
      method: 'POST',
      body: { token, new_password: newPassword }
    });
  }

  // Get team members for tenant
  async getTeamMembers(tenantId) {
    return this.request(`/auth/team/${tenantId}`);
  }

  // Update team member role
  async updateTeamMemberRole(userId, newRole) {
    return this.request(`/auth/team/${userId}/role?new_role=${newRole}`, {
      method: 'PUT'
    });
  }

  // Deactivate team member
  async deactivateTeamMember(userId) {
    return this.request(`/auth/team/${userId}`, {
      method: 'DELETE'
    });
  }
}

const api = new ApiService();
export default api;
